import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Input, Label, Screen, Text } from "@andrum/ui";
import { ResetPasswordSchema } from "@andrum/schemas";
import { requestPasswordReset } from "@/features/auth/api/auth-api";
import { isAuthEnforced } from "@/lib/supabase/client";

export function ForgotPasswordScreen() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError(null);
    const parsed = ResetPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("errorGeneric"));
      return;
    }

    if (!isAuthEnforced()) {
      setSent(true);
      return;
    }

    setLoading(true);
    const result = await requestPasswordReset(parsed.data.email);
    setLoading(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    setSent(true);
  };

  return (
    <Screen className="flex min-h-full flex-col justify-center">
      <Text variant="title" as="h1">
        {t("auth.forgotTitle")}
      </Text>
      <Text variant="muted" className="mt-2">
        {t("auth.welcomeLead")}
      </Text>

      {sent ? (
        <div className="mt-8 space-y-4">
          <div className="rounded-[18px] border border-border bg-surface-muted p-4">
            <Text variant="body">{t("auth.sendReset")}</Text>
          </div>
          <Link
            to="/login"
            className="block text-center font-medium text-primary"
          >
            {t("auth.backToLogin")}
          </Link>
        </div>
      ) : (
        <form
          className="mt-8 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
        >
          <div>
            <Label htmlFor="reset-email">{t("auth.email")}</Label>
            <Input
              id="reset-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@exempel.se"
            />
          </div>

          {error ? (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          ) : null}

          <Button fullWidth type="submit" loading={loading}>
            {t("auth.sendReset")}
          </Button>

          <Link
            to="/login"
            className="mt-2 block text-center text-sm text-foreground-muted"
          >
            {t("auth.backToLogin")}
          </Link>
        </form>
      )}
    </Screen>
  );
}
